import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { getPushConnector, getPushConnectors, writeData } from "../api";
import { useAppId, useSelectedPage } from "../hook";
import { ActionType, RootState } from "../reducer";
import {
  PushConnector,
  PushConnectorCreated,
  PushConnectorMap,
  PushConnectorObject,
  initialPushConnectorForm,
} from "../types/pushConnector";
import { usePushConnectorForm } from "./pushConnector/edit";
import { useAppIdMap } from "./app";

export const useSelectedPushConnector: () => [
  PushConnectorCreated | undefined,
  (selectedPushConnector: string | undefined) => void,
] = () => {
  const selectedPushConnectorId = useSelector(
    (state: RootState) => state.ui.selectedPushConnector,
  );
  const selectedPushConnector = useSelector((state: RootState) =>
    selectedPushConnectorId
      ? state.connectors.pushConnectors[selectedPushConnectorId]
      : undefined,
  );
  const dispatch = useDispatch();
  const setSelectedPushConnector = (selectedPushConnector?: string) => {
    dispatch({
      type: ActionType.SET_SELECTED_PUSH_CONNECTOR,
      selectedPushConnector,
    });
  };
  return [selectedPushConnector, setSelectedPushConnector];
};

export const usePushConnector = (pushConId: string | undefined) => {
  const [pushConnectors] = usePushConnectors();
  const { appId } = useAppIdMap();
  const addPushConnectors = useAddPushConnectors();
  const pushConnector = pushConId ? pushConnectors[pushConId] : undefined;
  const pushConObject: PushConnectorObject | undefined = pushConnector
    ? {
        "app-id": appId!,
        "push-con-id": pushConnector.id,
        "push-con-spec": pushConnector.data,
        type: "omega/query-omega/push-connector/PushConnector",
      }
    : undefined;
  const setPushConnector = (pushCon: PushConnectorObject) => {
    addPushConnectors([pushCon]);
    writeData(pushCon);
  };
  return { pushConnector: pushConObject, setPushConnector };
};

export const usePushConnectors: () => [
  PushConnectorMap,
  (setPushConnectors: PushConnectorMap) => void,
] = () => {
  const pushConnectors = useSelector(
    (state: RootState) => state.connectors.pushConnectors,
  );
  const [selectedPushConnector, setSelectedPushConnector] =
    useSelectedPushConnector();
  const [selectedPage, setSelectedPage] = useSelectedPage();
  const dispatch = useDispatch();
  const setPushConnectors = (pushConnectors: PushConnectorMap) => {
    dispatch({
      type: ActionType.SET_PUSH_CONNECTORS,
      pushConnectors,
    });
  };
  return [pushConnectors, setPushConnectors];
};

export const useAddPushConnectors = () => {
  const [pushConnectors, setPushConnectors] = usePushConnectors();

  return (newPushConnectors: PushConnectorObject[]) => {
    const toPushConsCreated: PushConnectorCreated[] = newPushConnectors.map(
      (con) => ({
        data: con["push-con-spec"],
        id: con["push-con-id"],
        type: "omega/connector/push-connector/PushConnectorCreated",
        description: con["push-con-spec"]["description"],
        name: con["push-con-spec"]["name"],
      }),
    );
    const newConnectorsMap = Object.fromEntries(
      toPushConsCreated.map((connector) => [connector.id, connector]),
    );
    setPushConnectors({ ...pushConnectors, ...newConnectorsMap });
  };
};

export const useHandlePushConnectorsResponse: () => (res: any) => void = () => {
  const [pushConnectors, setPushConnectors] = usePushConnectors();
  // const [_, setSelectedPushConnector] = useSelectedPushConnector();

  return (res: any) => {
    const connectorsArray = res?.connectors as PushConnectorCreated[];
    const connectorsMap = Object.fromEntries(
      connectorsArray.map((connector) => [connector.id, connector]),
    );
    // setSelectedPushConnector(connectorsArray[0].id);
    setPushConnectors(connectorsMap);
  };
};

export const useFetchPushConnectors: () => () => void = () => {
  const handlePushConnectorsResponse = useHandlePushConnectorsResponse();
  const [appId, setAppId] = useAppId();

  return () => {
    getPushConnectors({ appId: appId! }).then(handlePushConnectorsResponse);
  };
};

export const useFetchPushConnectorListener = (pushConId: string | undefined) => {
  const addPushConnectors = useAddPushConnectors();
  const { appId } = useAppIdMap();
  useEffect(() => {
    if (!pushConId || !appId) return;
    getPushConnector({ appId, pushConId }).then((pushCon) => {
      addPushConnectors([pushCon]);
    });
  }, [pushConId, appId]);
};

export const usePushConnectorRouteParamListener: () => void = () => {
  const { "push-connector-id": pushConnectorIdParam } = useParams();
  const [pushConnectorForm, setPushConnectorForm] = usePushConnectorForm();
  const [selectedPushConnector, setSelectedPushConnector] =
    useSelectedPushConnector();
  const [appId] = useAppId();
  useEffect(() => {
    if (!appId) return;
    setSelectedPushConnector(pushConnectorIdParam);
  }, [appId, pushConnectorIdParam]);
  useEffect(() => {
    return () => {
      setSelectedPushConnector(undefined);
      setPushConnectorForm(initialPushConnectorForm);
    };
  }, []);
};

export const usePushConnectorViewPath: () => (
  pushConnector: PushConnector,
) => string = () => {
  const [appId] = useAppId();

  return (pushConnector) => {
    if (!appId) return "";
    return `/app/${appId}/secret/${pushConnector.id}`;
  };
};

export const usePushConnectorEditPath: () => (
  pushConnector: PushConnector,
) => string = () => {
  const [appId] = useAppId();

  return (pushConnector) => {
    return `/app/${appId}/secret/${pushConnector.id}/edit`;
  };
};

export const usePushConnectorAddPath: () => string = () => {
  const [appId] = useAppId();
  return `/app/${appId}/secret/add`;
};
